import { FC, useMemo } from 'react'
import { CellProps } from 'react-table'
import clsx from 'clsx'
import { IconPaths, TipoReceitaSncr } from '_metronic/helpers'
import { TFunction, useT } from '_metronic/i18n/Metronici18n'
import {
  Button,
  CustomTable,
  PlainHeader,
  StackedCell,
  StatusDot,
  StatusDotTone,
  TableColumn,
} from 'app/components'
import { ROTULO_TIPO_RECEITA_SNCR, SncrSaldoTipo } from '../core/_models'
import { CHAVE_DESCRICAO_TIPO_RECEITA, SncrTipoReceitaDot } from './SncrTipoReceita'
import styles from './styles/SncrNotificacoesLista.module.css'

type IProps = {
  saldos: SncrSaldoTipo[]
  /** Integração desligada nesta plataforma: o botão fica desabilitado com explicação. */
  habilitado: boolean
  onRetirar: (tipo: TipoReceitaSncr) => void
}

/** Linha da tabela: um tipo com saldo, ou a talidomida, que só aparece (sem saldo nem ação). */
type LinhaNotificacao = {
  sigla: string
  rotulo: string
  descricao?: string
  saldo?: SncrSaldoTipo
}

type Situacao = 'alerta' | 'reservado' | 'neutro' | 'indisponivel'

const situacaoDe = ({ saldo }: LinhaNotificacao): Situacao => {
  if (!saldo) return 'indisponivel'
  if (saldo.alerta) return 'alerta'
  return saldo.saldo > 0 ? 'reservado' : 'neutro'
}

const TOM_SITUACAO: Record<Situacao, StatusDotTone> = {
  alerta: 'warning',
  reservado: 'success',
  neutro: 'neutral',
  indisponivel: 'neutral',
}

const rotuloSituacao = (t: TFunction, linha: LinhaNotificacao, situacao: Situacao) => {
  switch (situacao) {
    case 'alerta':
      return t('sncr.nr.abaixo_minimo', { limiar: linha.saldo?.limiarAlerta })
    case 'reservado':
      return t('sncr.nr.reservados')
    case 'neutro':
      return t('sncr.nr.sem_reserva')
    default:
      return t('sncr.nr.nao_disponivel')
  }
}

// Talidomida: documento próprio, com responsável pelo paciente; fora desta plataforma por ora.
const SIGLA_TALIDOMIDA = 'NRT'

export const sncrNotificacoesColumns = (
  t: TFunction,
  habilitado: boolean,
  onRetirar: (tipo: TipoReceitaSncr) => void
): ReadonlyArray<TableColumn<LinhaNotificacao>> => [
  {
    Header: () => <PlainHeader>{t('sncr.nr.col_tipo')}</PlainHeader>,
    id: 'tipo',
    Cell: ({ row }: CellProps<LinhaNotificacao>) => {
      const { sigla, rotulo, descricao, saldo } = row.original
      return (
        <StackedCell
          primary={
            <>
              {saldo ? <SncrTipoReceitaDot tipo={saldo.tipo} /> : <StatusDot label={sigla} />}
              {descricao && <span className='text-gray-700 ms-2'>{descricao}</span>}
            </>
          }
          secondary={rotulo}
        />
      )
    },
  },
  {
    Header: () => <PlainHeader>{t('sncr.nr.col_reserva')}</PlainHeader>,
    id: 'reserva',
    cellWidth: '110px',
    align: 'end',
    Cell: ({ row }: CellProps<LinhaNotificacao>) => {
      const situacao = situacaoDe(row.original)
      return (
        <StackedCell
          tabular
          primary={
            row.original.saldo && (
              <span
                className={clsx(
                  'fw-bold',
                  situacao === 'alerta' && styles.valorAlerta,
                  situacao === 'reservado' && styles.valorOk
                )}
              >
                {row.original.saldo.saldo}
              </span>
            )
          }
        />
      )
    },
  },
  {
    Header: () => <PlainHeader>{t('sncr.nr.col_situacao')}</PlainHeader>,
    id: 'situacao',
    cellWidth: '260px',
    Cell: ({ row }: CellProps<LinhaNotificacao>) => {
      const situacao = situacaoDe(row.original)
      return (
        <StatusDot
          label={rotuloSituacao(t, row.original, situacao)}
          tone={TOM_SITUACAO[situacao]}
        />
      )
    },
  },
  {
    Header: () => <PlainHeader>{t('sncr.nr.col_acoes')}</PlainHeader>,
    id: 'actions',
    cellWidth: '190px',
    align: 'end',
    Cell: ({ row }: CellProps<LinhaNotificacao>) => {
      const { saldo } = row.original
      if (!saldo) return null
      const alerta = situacaoDe(row.original) === 'alerta'
      // O Button é d-flex (ocupa a célula toda): o text-align da coluna não o alinha.
      return (
        <div className='d-flex justify-content-end'>
          <Button
            size='btn-sm'
            className={clsx(alerta && 'btn-primary')}
            variant={alerta ? undefined : 'secondary-outline'}
            icon={IconPaths.Add}
            onClick={() => onRetirar(saldo.tipo)}
            disabled={!habilitado}
            title={habilitado ? undefined : t('sncr.card.desabilitado')}
            ariaLabel={t('sncr.alerta.retirar_aria_nr', { sigla: saldo.tipo })}
          >
            {t('sncr.alerta.retirar_nr')}
          </Button>
        </div>
      )
    },
  },
]

/**
 * Notificações de Receita num card só, com uma linha por tipo: são números avulsos que hoje só
 * ficam reservados, então não merecem um card cada como os blocos. Só alerta quem já retirou o
 * tipo; os demais ficam neutros ("sem reserva"). Talidomida aparece desabilitada para ninguém
 * procurar onde está.
 */
export const SncrNotificacoesLista: FC<IProps> = ({ saldos, habilitado, onRetirar }) => {
  const { t } = useT()

  const linhas = useMemo<LinhaNotificacao[]>(
    () => [
      ...saldos.map((saldo) => ({
        sigla: saldo.tipo,
        rotulo: ROTULO_TIPO_RECEITA_SNCR[saldo.tipo],
        descricao: t(CHAVE_DESCRICAO_TIPO_RECEITA[saldo.tipo]),
        saldo,
      })),
      { sigla: SIGLA_TALIDOMIDA, rotulo: t('sncr.nr.talidomida') },
    ],
    [saldos, t]
  )
  const columns = useMemo(
    () => sncrNotificacoesColumns(t, habilitado, onRetirar),
    [t, habilitado, onRetirar]
  )

  return (
    <section className={styles.card} aria-labelledby='sncr-nr-titulo'>
      {/* Faixa de cabeçalho própria: é o que separa este painel dos cards de bloco no olho. */}
      <header className={styles.cabecalho}>
        <h3 id='sncr-nr-titulo' className={styles.titulo}>
          {t('sncr.nr.titulo')}
        </h3>
        <p className={styles.subtitulo}>{t('sncr.nr.intro')}</p>
      </header>
      <CustomTable<LinhaNotificacao>
        id='kt_table_sncr_notificacoes'
        ariaLabel={t('sncr.nr.tabela_aria')}
        data={linhas}
        columns={columns}
      />
    </section>
  )
}
